import { formatExpenseMoney, expenseValidationText } from './expensePresentation';
import { formatPlainDate, formatDateTimeValue } from '../../i18n/travelFormat';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ui';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { journeyExpenseApi } from '../../api/journeyExpenseApi';
import { BUDGET_CATEGORY_CODES, budgetCategoryLabel } from '../finance/budgetCategory';
import { createClientRequestId } from './journeyExpenseModel';
import { coreDraft, corePayload, coreAmountPreview, coreLinesPreview, defaultCoreDay, hasCoreTimezone, unwrapCore } from './expenseCoreModel';
import './expense-core.css';
import CostClosingPanel from './CostClosingPanel';

/** 既有 Expense 頁面的三層介面；付款與分攤都由使用者明確選取。 */
export default function ExpenseCorePanel({
  groupTripId
}) {
  const {
    t,
    tf,
    intlLocale
  } = useTravelTranslation();
  const money = (value, currency) => formatExpenseMoney(value, currency, intlLocale);
  const date = value => formatPlainDate(value, intlLocale) || t('travel.expense.s06.dateNotSet');
  const time = value => formatDateTimeValue(value, intlLocale) || t('travel.expense.s06.pending');
  const {
    journeyId
  } = useParams();
  const {
    user
  } = useAuth();
  const identity = String(user?.memberId ?? user?.id ?? '');
  const [ledger, setLedger] = useState(null),
    [day, setDay] = useState(''),
    [draft, setDraft] = useState(null),
    [entryId, setEntryId] = useState(null),
    [tab, setTab] = useState('記帳'),
    [error, setError] = useState(''),
    [notice, setNotice] = useState(''),
    [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false);
  const scopeKey = `${journeyId}:${groupTripId}:${identity}`;
  const activeScope = useRef(scopeKey);
  activeScope.current = scopeKey;
  const [loadedFor, setLoadedFor] = useState('');
  const [stale, setStale] = useState(false);
  const [showHistorical, setShowHistorical] = useState(false);
  const [contextDialog, setContextDialog] = useState(false);
  const [canRetryOriginal, setCanRetryOriginal] = useState(false);
  const generation = useRef(0),
    lock = useRef(false),
    pending = useRef(null);
  const load = useCallback(async () => {
    const g = ++generation.current;
    try {
      const data = unwrapCore(await journeyExpenseApi.coreLedger(journeyId, groupTripId));
      if (g !== generation.current || activeScope.current !== scopeKey) return null;
      setLoadedFor(scopeKey);
      setLedger(data);
      setDay(old => data.days.some(x => String(x.dayId) === old) ? old : defaultCoreDay(data.days));
      setError('');
      return data;
    } catch (e) {
      if (g === generation.current && activeScope.current === scopeKey) {
        if ([401, 403].includes(e?.response?.status)) {
          setLedger(null);
          setLoadedFor('');
          setDraft(null);
          pending.current = null;
        }
        setError([401,403].includes(e?.response?.status)?"travel.expense.s06.deniedRead":"travel.expense.s06.unableToLoadTheLedgerCheckAccess");
      }
      return null;
    }
  }, [journeyId, groupTripId, scopeKey]);
  useEffect(() => {
    lock.current = false;
    setBusy(false);
    setStale(false);
    setContextDialog(false);
    setShowHistorical(false);
    setCanRetryOriginal(false);
    setLedger(null);
    setDraft(null);
    setEntryId(null);
    setUncertain(false);
    pending.current = null;
    load();
    return () => {
      generation.current += 1;
    };
  }, [load, identity]);
  const set = (key, value) => {
    if (!lock.current && !uncertain) setDraft(x => ({
      ...x,
      [key]: value
    }));
  };
  const open = entry => {
    if (lock.current || uncertain) return;
    setEntryId(entry ? String(entry.expenseId) : null);
    setDraft(coreDraft(entry, day));
    setShowHistorical(Boolean(entry && (ledger.travelers || []).some(t => t.historical && [...(entry.core.payments || []), ...(entry.core.shares || [])].some(x => String(x.travelerId) === String(t.travelerId)))));
    setTab(entry ? '明細' : '記帳');
    setError('');
    setNotice('');
    setCanRetryOriginal(false);
    pending.current = null;
  };
  const submit = async (confirm, contextApproved = false) => {
    if (lock.current || !ledger || !draft || uncertain || stale) return;
    if (entryId && !confirm && !contextApproved) {
      const original = ledger.entries.find(x => String(x.expenseId) === entryId);
      const nextDay = ledger.days.find(x => String(x.dayId) === draft.dayId);
      if (original && (String(original.core.dayId) !== draft.dayId || original.core.dayDate !== nextDay?.dayDate || original.core.timezone !== nextDay?.timezone || String(original.occurredLocalAt || '') !== String(draft.occurredLocalAt || ''))) {
        setContextDialog(true);
        return;
      }
    }
    setCanRetryOriginal(false);
    setError('');
    setNotice('');
    const signature = JSON.stringify({
      draft,
      entryId,
      confirm
    });
    if (!pending.current || pending.current.signature !== signature) pending.current = {
      key: createClientRequestId(),
      signature
    };
    let body;
    try {
      body = {
        ...corePayload(draft, ledger, pending.current.key),
        contextChangeConfirmed: confirm || contextApproved
      };
    } catch (e) {
      setError(expenseValidationText(e, t, tf));
      return;
    }
    pending.current = {
      ...pending.current,
      body,
      entryId,
      confirm
    };
    lock.current = true;
    setBusy(true);
    const mutationScope = scopeKey;
    try {
      const raw = confirm ? await journeyExpenseApi.coreConfirm(journeyId, groupTripId, entryId, body) : entryId ? await journeyExpenseApi.coreSave(journeyId, groupTripId, entryId, body) : await journeyExpenseApi.coreCreate(journeyId, groupTripId, body);
      if (mutationScope !== activeScope.current) return;
      const saved = unwrapCore(raw);
      pending.current = null;
      setEntryId(String(saved.expenseId));
      setDraft(coreDraft(saved, day));
      setNotice(confirm ? "travel.expense.s06.expenseConfirmedAndIncludedInSettlement" : "travel.expense.s06.draftSavedNoDebtHasBeenCreated");
      setTab(confirm ? '結算' : '明細');
      await load();
    } catch (e) {
      if (mutationScope !== activeScope.current) return;
      const status = e?.response?.status;
      if (!status || status >= 500) {
        setUncertain(true);
        setError("travel.expense.s06.resultUnconfirmedCheckTheOriginalRequestBefore");
      } else {
        pending.current = null;
        if (status === 403) {
          setLedger(null);
          setLoadedFor('');
          setDraft(null);
        }
        if (status === 409) setStale(true);
        setError(status === 409 ? "travel.expense.s06.theVersionHasChangedReloadAndReview" : status === 403 ? "travel.expense.s06.youDoNotHavePermissionToManage" : "travel.expense.s06.unableToSaveCheckCurrencyPrecisionPayment");
      }
    } finally {
      if (mutationScope === activeScope.current) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  const reconcile = async () => {
    if (lock.current) return;
    const reconciliationScope = scopeKey;
    lock.current = true;
    setBusy(true);
    try {
      const data = await load();
      if (!data) return;
      const p = pending.current;
      const saved = data.entries.find(e => e.core.operationKey === p?.key || (!p?.entryId && e.createRequestId === p?.key));
      if (saved) {
        setEntryId(String(saved.expenseId));
        setDraft(coreDraft(saved, day));
        setNotice("travel.expense.s06.serverStateRetrievedReviewItBeforeContinuing");
        pending.current = null;
        setCanRetryOriginal(false);
        setUncertain(false);
      } else {
        setCanRetryOriginal(true);
        setError("travel.expense.s06.theOriginalResultWasNotFoundYou");
      }
    } finally {
      if (reconciliationScope === activeScope.current) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  const retryOriginal = async () => {
    const p = pending.current;
    if (lock.current || !p?.body || !canRetryOriginal) return;
    const retryScope = scopeKey;
    lock.current = true;
    setBusy(true);
    setCanRetryOriginal(false);
    try {
      const raw = p.confirm ? await journeyExpenseApi.coreConfirm(journeyId, groupTripId, p.entryId, p.body) : p.entryId ? await journeyExpenseApi.coreSave(journeyId, groupTripId, p.entryId, p.body) : await journeyExpenseApi.coreCreate(journeyId, groupTripId, p.body);
      if (retryScope !== activeScope.current) return;
      const saved = unwrapCore(raw);
      pending.current = null;
      setUncertain(false);
      setEntryId(String(saved.expenseId));
      setDraft(coreDraft(saved, day));
      setNotice("travel.expense.s06.theOriginalRequestSucceededWithoutCreatingA");
      setTab(p.confirm ? '結算' : '明細');
      await load();
    } catch (e) {
      if (retryScope !== activeScope.current) return;
      const status = e?.response?.status;
      if (status === 403) {
        pending.current = null;
        setUncertain(false);
        setLedger(null);
        setLoadedFor('');
        setDraft(null);
        setError("travel.expense.s06.youDoNotHavePermissionToManage");
      } else if (status && status < 500 && status !== 409) {
        pending.current = null;
        setUncertain(false);
        setError("travel.expense.s06.theOriginalRequestWasRejectedCheckThe");
      } else setError("travel.expense.s06.theOriginalResultStillNeedsCheckingNo");
    } finally {
      if (retryScope === activeScope.current) {
        lock.current = false;
        setBusy(false);
      }
    }
  };
  if (!ledger || loadedFor !== scopeKey) return <main lang={intlLocale} className="expense-core"><h1>{t("travel.expense.s06.travelExpenses")}</h1><p role={error ? 'alert' : 'status'}>{error ? t(error) : t('travel.expense.s06.loadingLedger')}</p>{error && <button onClick={load}>{t("travel.expense.s06.reload")}</button>}<Link to={`/journeys/${journeyId}`}>{t("travel.expense.s06.backToJourney")}</Link></main>;
  const selected = ledger.days.find(x => String(x.dayId) === day);
  const allChoices = ledger.travelers || [];
  const choices = allChoices.filter(t => !t.historical || showHistorical);
  const label = id => allChoices.find(x => String(x.travelerId) === String(id))?.label || tf("travel.expense.s06.traveler", {
    value0: id
  });
  const toggle = (field, id, checked) => set(field, checked ? [...draft[field], {
    travelerId: String(id),
    amount: '0'
  }] : draft[field].filter(x => x.travelerId !== String(id)));
  const amount = (field, id, value) => set(field, draft[field].map(x => x.travelerId === String(id) ? {
    ...x,
    amount: value
  } : x));
  const immutable = entryId && ledger.entries.find(x => String(x.expenseId) === entryId)?.core.stage === 'CONFIRMED';
  return <main lang={intlLocale} className="expense-core" aria-busy={busy}><header><p>{t("travel.expense.s06.travelExpenses")}</p><h1>{t("travel.expense.s06.expensesAndSharing")}</h1><Link to={`/journeys/${journeyId}/expenses`}>{t("travel.expense.s06.previousExpenses")}</Link></header>
    <nav aria-label={t("travel.expense.s06.expenseNavigation")}>{['記帳', '明細', '結算'].map(x => <button key={x} aria-pressed={tab === x} onClick={() => setTab(x)}>{t({
          記帳: 'travel.expense.s06.entry',
          明細: 'travel.expense.s06.details',
          結算: 'travel.expense.s06.settlement'
        }[x])}</button>)}</nav>
    {tab!=='結算'&&<>    <label>{t("travel.expense.s06.tripDay")}<select value={day} disabled={busy || uncertain} onChange={e => {
        setDay(e.target.value);
        if (draft && !entryId) set('dayId', e.target.value);
      }}>{ledger.days.map((x, i) => <option key={x.dayId} value={x.dayId}>{tf("travel.expense.s06.day", {
            value0: i + 1,
            value1: date(x.dayDate)
          })}</option>)}</select></label>
    {!hasCoreTimezone(selected?.timezone) && <p role="status">{t("travel.expense.s06.theJourneyTimeZoneIsMissingOr")}</p>}
    <p>{tf("travel.expense.s06.dayIsTheDefaultOutsideTheTrip", {
        value0: date(selected?.dayDate),
        value1: ledger.currencyCode
      })}</p>
</>}
    {busy&&<p role="status">{t('travel.expense.s06.busy')}</p>}
    {error && <p role="alert">{t(error)}</p>}{notice && <p role="status">{t(notice)}</p>}
    {stale && <button disabled={busy} onClick={async () => {
      const data = await load();
      if (!data) return;
      const found = data.entries.find(x => String(x.expenseId) === entryId);
      if (entryId && !found) {
        setError("travel.expense.s06.thisExpenseIsNoLongerAccessibleReopen");
        return;
      }
      if (found) setDraft(coreDraft(found, day));
      setStale(false);
      setNotice("travel.expense.s06.latestServerDataAppliedReviewItBefore");
    }}>{t("travel.expense.s06.reloadAndApplyServerExpense")}</button>}
    {uncertain ? <button disabled={busy} onClick={reconcile}>{t("travel.expense.s06.checkOriginalRequest")}</button> : tab!=='結算'?<button disabled={busy} onClick={load}>{t('travel.expense.s06.reload')}</button>:null}
    {uncertain && canRetryOriginal && <button disabled={busy} onClick={retryOriginal}>{t("travel.expense.s06.retryWithOriginalRequestKey")}</button>}
    {tab === '記帳' && <section aria-label={t("travel.expense.s06.quickEntry")}>{ledger.canEdit && (!draft || entryId) && <button disabled={busy || uncertain || !day} onClick={() => open(null)}>{t("travel.expense.s06.addExpenseQuickly")}</button>}
      {ledger.canEdit && draft && !entryId && <form onSubmit={e => {
        e.preventDefault();
        submit(false);
      }}><label>{t("travel.expense.s06.amount")}<input inputMode="decimal" value={draft.baseAmount} disabled={busy || uncertain} onChange={e => set('baseAmount', e.target.value)} /></label><button disabled={busy || uncertain}>{t("travel.expense.s06.saveDraft")}</button><p>{t("travel.expense.s06.enterOnlyTheAmountNowAddPayments")}</p></form>}
      {!ledger.entries.some(x=>String(x.core.dayId)===day||x.dayDeleted)&&<p>{t('travel.expense.s06.emptyDay')}</p>}<ul>{ledger.entries.filter(x => String(x.core.dayId) === day || x.dayDeleted).map(e => <li key={e.expenseId}><strong>{e.title && e.title !== "未命名費用" ? e.title : t("travel.expense.s06.untitledExpense")}</strong><span>{e.core.stage === 'DRAFT' ? t("travel.expense.s06.draftNoDebtCreated") : t("travel.expense.s06.confirmed")}</span>{e.dayDeleted && <span>{tf("travel.expense.s06.originalTripDayDeleted", {
              value0: date(e.core.dayDate)
            })}</span>}{!e.dayDeleted && ledger.days.find(d => String(d.dayId) === String(e.core.dayId))?.dayDate !== e.core.dayDate && <span>{tf("travel.expense.s06.tripDateChangedOriginalExpenseDate", {
              value0: date(e.core.dayDate)
            })}</span>}<p>{tf('travel.expense.s06.entryAmount', {
              amount: money(e.totalAmount ?? e.core.baseAmount, e.core.currency),
              state: t(e.core.payments.length ? 'travel.expense.s06.paymentDetailsAdded' : 'travel.expense.s06.payerNotSet')
            })}</p><button disabled={busy || uncertain} onClick={() => open(e)}>{t("travel.expense.s06.viewDetails")}</button></li>)}</ul>
    </section>}
    {tab === '明細' && <section aria-label={t("travel.expense.s06.expenseDetails")}>{!draft ? <p>{t("travel.expense.s06.chooseAnExpenseFromTheListFirst")}</p> : <form onSubmit={e => e.preventDefault()}><fieldset disabled={busy || uncertain || stale || immutable || !ledger.canEdit}>
      <label>{t("travel.expense.s06.expenseName")}<input value={draft.title} onChange={e => set('title', e.target.value)} /></label>
      <p>{tf('travel.expense.s06.entryAmount',{amount:money(coreAmountPreview(draft,ledger.minorUnits),ledger.currencyCode),state:immutable?t('travel.expense.s06.confirmed'):t('travel.expense.s06.draftNoDebtCreated')})}</p><details className="expense-core__advanced"><summary>{t('travel.expense.s06.advanced')}</summary>      <label>{t("travel.expense.s06.tripDay")}<select value={draft.dayId} onChange={e => set('dayId', e.target.value)}>{!ledger.days.some(d => String(d.dayId) === draft.dayId) && <option value={draft.dayId}>{t("travel.expense.s06.originalTripDayDeletedChooseANew")}</option>}{ledger.days.map((d, i) => <option key={d.dayId} value={d.dayId}>{tf("travel.expense.s06.day", {
                    value0: i + 1,
                    value1: date(d.dayDate)
                  })}</option>)}</select></label>
      {[['baseAmount', t("travel.expense.s06.baseAmount")], ['serviceFee', t("travel.expense.s06.serviceFee")], ['discount', t("travel.expense.s06.discount")]].map(([k, text]) => <label key={k}>{text}<input inputMode="decimal" value={draft[k]} onChange={e => set(k, e.target.value)} /></label>)}
      <p>{tf("travel.expense.s06.totalBaseFeeDiscountPaymentsAndShares", {
                value0: coreAmountPreview(draft, ledger.minorUnits) === null ? t("travel.expense.s06.checkAmount") : money(coreAmountPreview(draft, ledger.minorUnits), ledger.currencyCode),
                value1: ledger.currencyCode
              })}</p>
      <label>{t("travel.expense.s06.category")}<select value={draft.categoryCode} onChange={e => set('categoryCode', e.target.value)}><option value="">{t("travel.expense.s06.addLater")}</option>{BUDGET_CATEGORY_CODES.map(c => <option key={c} value={c}>{budgetCategoryLabel(c, t)}</option>)}</select></label>
      <label>{tf('travel.expense.s06.timeWithZone', {
                zone: selected?.timezone || t('travel.expense.s06.journeyLocalTime')
              })}<input type="datetime-local" value={draft.occurredLocalAt} onChange={e => set('occurredLocalAt', e.target.value)} /></label>
      <label>{t("travel.expense.s06.note")}<textarea value={draft.note} onChange={e => set('note', e.target.value)} /></label>
      <label><input type="checkbox" checked={showHistorical} onChange={e => setShowHistorical(e.target.checked)} />{t("travel.expense.s06.showDepartedTravelersForHistoricalExpenses")}</label><p>{t("travel.expense.s06.historicalEntriesRequireVerifiedActualTimesCosts")}</p><h2>{t("travel.expense.s06.sharedPayments")}</h2><p>{t("travel.expense.s06.selectTheActualPayersAndEnterEach")}</p>{choices.map(t => <div className="expense-core__person" key={t.travelerId}><label><input type="checkbox" checked={draft.payments.some(x => x.travelerId === String(t.travelerId))} onChange={e => toggle('payments', t.travelerId, e.target.checked)} />{t.label}</label>{draft.payments.some(x => x.travelerId === String(t.travelerId)) && <input aria-label={tf("travel.expense.s06.paymentAmount", {
                value0: t.label
              })} inputMode="decimal" value={draft.payments.find(x => x.travelerId === String(t.travelerId)).amount} onChange={e => amount('payments', t.travelerId, e.target.value)} />}</div>)}
      <p>{tf("travel.expense.s06.totalPaid", {
                value0: coreLinesPreview(draft.payments, ledger.minorUnits) === null ? t("travel.expense.s06.checkAmount") : money(coreLinesPreview(draft.payments, ledger.minorUnits), ledger.currencyCode),
                value1: ledger.currencyCode
              })}</p><h2>{t("travel.expense.s06.actualParticipantsAndShares")}</h2><label>{t("travel.expense.s06.sharingMethod")}<select value={draft.method} onChange={e => set('method', e.target.value)}><option value="EQUAL">{t("travel.expense.s06.equalShares")}</option><option value="EXACT_AMOUNT">{t("travel.expense.s06.exactAmountsZeroAllowed")}</option></select></label><p>{t("travel.expense.s06.unselectedTravelersOweNothingTheWholeGroup")}</p>
      {choices.map(t => <div className="expense-core__person" key={t.travelerId}><label><input type="checkbox" checked={draft.shares.some(x => x.travelerId === String(t.travelerId))} onChange={e => toggle('shares', t.travelerId, e.target.checked)} />{t.label}</label>{draft.method === 'EXACT_AMOUNT' && draft.shares.some(x => x.travelerId === String(t.travelerId)) && <input aria-label={tf("travel.expense.s06.shareAmount", {
                value0: t.label
              })} inputMode="decimal" value={draft.shares.find(x => x.travelerId === String(t.travelerId)).amount} onChange={e => amount('shares', t.travelerId, e.target.value)} />}</div>)}
      <p>{tf("travel.expense.s06.totalShares", {
                value0: draft.method === 'EQUAL' ? draft.shares.length ? money(coreAmountPreview(draft, ledger.minorUnits),ledger.currencyCode) : t('travel.expense.s06.noTravelersSelected') : money(coreLinesPreview(draft.shares, ledger.minorUnits),ledger.currencyCode),
                value1: ledger.currencyCode
              })}</p></details><div className="expense-core__actions"><button type="button" onClick={() => submit(false)}>{t("travel.expense.s06.saveDraft")}</button><button type="button" disabled={!entryId} onClick={() => submit(true)}>{t("travel.expense.s06.confirmExpenseTripDayAndTimeFor")}</button></div></fieldset>
      {immutable && <p>{t("travel.expense.s06.thisExpenseIsConfirmedPaymentAndShare")}</p>}
      <h3>{t("travel.expense.s06.savedPaymentsAndShares")}</h3>{[...draft.payments.map(x => ({
          ...x,
          kind: t("travel.expense.s06.payment")
        })), ...draft.shares.map(x => ({
          ...x,
          kind: t("travel.expense.s06.share")
        }))].map((x, i) => <p key={`${x.kind}-${x.travelerId}-${i}`}>{tf('travel.expense.s06.savedLine', {
            person: label(x.travelerId),
            role: x.kind,
            amount: money(x.amount, ledger.currencyCode)
          })}</p>)}
    </form>}</section>}
    {tab === '結算' && <section aria-label={t("travel.expense.s06.officialSettlementSummary")}>
      <CostClosingPanel journeyId={journeyId} groupTripId={groupTripId} identity={identity} ledger={ledger} onChanged={load} /><details className="expense-core__advanced"><summary>{t("travel.expense.s06.travelerBalancesFromConfirmedExpenses")}</summary><p>{t("travel.expense.s06.draftsAreExcludedTheseBalancesDoNot")}</p>{ledger.balances.length ? <ul>{ledger.balances.map(b => <li key={b.reference.id}>{label(b.reference.id)}<p>{tf("travel.expense.s06.paidShareNet", {
              value0: money(b.paidAmount, ledger.currencyCode),
              value1: money(b.owedAmount, ledger.currencyCode),
              value2: money(b.netAmount, ledger.currencyCode),
              value3: ledger.currencyCode
            })}</p></li>)}</ul> : <p>{t("travel.expense.s06.noConfirmedExpensesYet")}</p>}<p>{t('travel.expense.s06.compatibility')}</p></details><details className="expense-core__advanced"><summary>{t("travel.expense.s06.previousExpensesAndAccounting")}</summary><p>{t("travel.expense.s06.originalPrecisionAndIdentityArePreservedThese")}</p><ul>{(ledger.previousEntries || []).map(e => <li key={e.expenseId}>{tf("travel.expense.s06.previousExpenseNoTripDay", {
            value0: e.title,
            value1: money(e.amount,e.currencyCode),
            value2: e.currencyCode
          })}</li>)}</ul><ul>{(ledger.previousBalances || []).map((b, i) => <li key={i}>{tf("travel.expense.s06.originalNet", {
            value0: b.reference.kind === 'LEGACY_MEMBER' ? t("travel.expense.s06.legacyMember") : t("travel.expense.s06.previousTraveler"),
            value1: b.reference.id,
            value2: money(b.netAmount,ledger.currencyCode)
          })}</li>)}</ul></details>
    </section>}
    <ConfirmDialog open={contextDialog} title={t("travel.expense.s06.confirmTripDayOrTimeChange")} description={tf("travel.expense.s06.previousDateTimeNewDateTimeOriginal", {
      value0: date(ledger.entries.find(x => String(x.expenseId) === entryId)?.core.dayDate),
      value1: time(ledger.entries.find(x => String(x.expenseId) === entryId)?.occurredLocalAt),
      value2: date(ledger.days.find(x => String(x.dayId) === draft?.dayId)?.dayDate),
      value3: time(draft?.occurredLocalAt)
    })} confirmLabel={t("travel.expense.s06.confirmChangeAndSaveDraft")} cancelLabel={t("travel.expense.s06.cancel")} loading={busy} onCancel={() => setContextDialog(false)} onConfirm={() => {
      setContextDialog(false);
      submit(false, true);
    }} />
  </main>;
}
